import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { useProjectTemplates } from '../../hooks/useProjectTemplates';
import type { ProjectTemplate } from '../../services/api/projectTemplates';

/**
 * Admin templates list (Phase 20 UI-04, D-01, D-02).
 *
 * Renders every non-deleted, non-legacy template (server filters
 * is_legacy=true; the hook also filters defensively per Phase 19's
 * getProjectTemplates implementation).
 *
 * Columns: Name, Description, State count.
 *
 * Note: the "in use" count is intentionally NOT shown on the LIST page —
 * it is only surfaced on the per-template edit page where it is computed
 * client-side from useProjects() (see ProjectTemplateEditPage). Showing it
 * on the list would require iterating useProjects() per template row which
 * is wasteful; the row-click pattern means admins always go through the
 * edit page before deleting anyway.
 */
export default function ProjectTemplatesPage() {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useProjectTemplates();

  const sortedTemplates = useMemo<ProjectTemplate[]>(() => {
    if (!templates) return [];
    // D-01 default sort: name asc (case-insensitive, locale-aware).
    return [...templates].sort((a, b) => a.name.localeCompare(b.name));
  }, [templates]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Project Templates</h1>
          <Button size="sm" onClick={() => navigate('/admin/project-templates/new')}>
            + New Template
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                State count
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedTemplates.length === 0 && (
              <tr>
                <td colSpan={3} className="px-6 py-6 text-center text-sm text-gray-500">
                  No templates yet — click + New Template to create one.
                </td>
              </tr>
            )}
            {sortedTemplates.map((tpl) => (
              <tr
                key={tpl.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/project-templates/${tpl.id}`)}
                data-testid={`template-row-${tpl.id}`}
              >
                <td className="px-6 py-3 text-sm font-medium text-gray-900">
                  {tpl.name}
                  {tpl.isBuiltIn && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                      Built-in
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-gray-600">{tpl.description || '-'}</td>
                <td className="px-6 py-3 text-sm text-gray-600 text-right">{tpl.states.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
